import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Collapse,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  List as ListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

interface ReadOnlyKeywordsPanelProps {
  keywords: string[];
  loading?: boolean;
  onDeleteKeyword?: (keyword: string) => void;
}

const ReadOnlyKeywordsPanel: React.FC<ReadOnlyKeywordsPanelProps> = ({
  keywords,
  loading = false,
  onDeleteKeyword,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  const filteredKeywords = useMemo(() => {
    if (!searchTerm.trim()) {
      return keywords;
    }
    return keywords.filter(keyword => 
      keyword.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [keywords, searchTerm]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleDeleteKeyword = (keyword: string) => {
    if (onDeleteKeyword) {
      onDeleteKeyword(keyword);
    }
  };

  return (
    <Card elevation={2}>
      {/* Clickable Header */}
      <Box
        onClick={handleToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {/* Expand/Collapse Icon */}
        <IconButton
          size="small"
          sx={{ 
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'transparent',
            }
          }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>

        {/* Keywords Title and Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ListIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Keywords
            </Typography>
            <Chip 
              label={searchTerm ? `${filteredKeywords.length}/${keywords.length}` : keywords.length} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            loaded from SERP analysis
          </Typography>
          
          {/* Search Field */}
          <TextField
            size="small"
            placeholder="Search keywords..."
            value={searchTerm}
            onChange={handleSearchChange}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{ 
              ml: 'auto',
              minWidth: 200,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearSearch();
                    }}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>


      {/* Collapsible Content */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ pt: 0 }}>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading previous configuration...
            </Typography>
          </Box>
        ) : keywords.length === 0 ? (
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 3, 
              textAlign: 'center',
              backgroundColor: 'action.hover'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No keywords found from SERP analysis
            </Typography>
          </Paper>
        ) : (
          <Paper 
            variant="outlined" 
            sx={{ 
              maxHeight: 400, 
              overflow: 'auto',
              backgroundColor: 'background.default'
            }}
          >
            {filteredKeywords.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No keywords match "{searchTerm}"
                </Typography>
              </Box>
            ) : (
              <List dense>
                {filteredKeywords.map((keyword, index) => (
                  <ListItem 
                    key={index}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    secondaryAction={
                      onDeleteKeyword && hoveredIndex === index ? (
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleDeleteKeyword(keyword)}
                          sx={{
                            color: 'error.main',
                            '&:hover': {
                              backgroundColor: 'error.light',
                              color: 'error.dark',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      ) : null
                    }
                    sx={{
                      borderRadius: 1,
                      backgroundColor: hoveredIndex === index ? 'action.hover' : 'transparent',
                    }}
                  >
                    <ListItemText 
                      primary={keyword}
                      primaryTypographyProps={{
                        variant: 'body2',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default ReadOnlyKeywordsPanel;

